import { type FiatCurrency, useSettings } from '@capsicum/common';
import { useState } from 'react';
import { type FieldError } from 'react-hook-form';

type TopupStateProps = {
	chzToFiatConversionRate?: string;
	fiatToChzConversionRate?: string;
	onCryptoValueChange: (amount: string) => void;
	onInputValueChange: (amount: string) => void;
	onSelectionChanged: (currency?: FiatCurrency) => void;
};

export const useTopupState = ({
	chzToFiatConversionRate,
	fiatToChzConversionRate,
	onCryptoValueChange,
	onInputValueChange,
	onSelectionChanged
}: TopupStateProps) => {
	const [cryptoCurrencyAmountText, setCryptoCurrencyAmountText] = useState<string>();
	const [defaultCurrencyAmountText, setDefaultCurrencyAmountText] = useState<string>();
	const [maxLimit, setMaxLimit] = useState<number>();
	const [minLimitFiat, setMinLimitFiat] = useState<number>();
	const [minLimitCrypto, setMinLimitCrypto] = useState<number>();
	const [maxCryptoLimit, setMaxCryptoLimit] = useState<number>();
	const [inputCryptoValue, setCryptoInputValue] = useState<string>();
	const [inputFiatValue, setFiatInputValue] = useState<string>('25');
	const [isFiatMode, setIsFiatMode] = useState<boolean>(true);
	const [error, setError] = useState<FieldError | undefined>(undefined);
	const [oldCurrency, setOldCurrency] = useState<FiatCurrency>();

	const {
		areSettingsLoading,
		settings: { isIAMTopupRequestEnabled }
	} = useSettings();

	const switchCurrencies = () => {
		setIsFiatMode(!isFiatMode);
	};

	const onFiatAmountChange = (amount: string) => {
		if (!fiatToChzConversionRate) return;

		const calculatedRate = (Number(amount) * Number(fiatToChzConversionRate)).toFixed(2);

		setFiatInputValue(amount);
		setCryptoInputValue(calculatedRate);
		onInputValueChange(amount);
		onCryptoValueChange(calculatedRate);
	};

	const onCryptoAmountChange = (amount: string) => {
		if (!chzToFiatConversionRate) return;

		const calculatedRate = (Number(amount) * Number(chzToFiatConversionRate)).toFixed(2);

		setCryptoInputValue(amount);
		setFiatInputValue(calculatedRate);
		onInputValueChange(calculatedRate);
		onCryptoValueChange(amount);
	};

	const onCurrencyChange = (previousCurrency?: FiatCurrency, currency?: FiatCurrency) => {
		setOldCurrency(previousCurrency);
		onSelectionChanged(currency);
	};

	return {
		cryptoCurrencyAmountText,
		defaultCurrencyAmountText,
		error,
		inputCryptoValue,
		inputFiatValue,
		isFiatMode,
		isIAMTopupEnabled: !areSettingsLoading && isIAMTopupRequestEnabled,
		maxCryptoLimit,
		maxLimit,
		minLimitCrypto,
		minLimitFiat,
		oldCurrency,
		onCryptoAmountChange,
		onCurrencyChange,
		onFiatAmountChange,
		setCryptoCurrencyAmountText,
		setCryptoInputValue,
		setDefaultCurrencyAmountText,
		setError,
		setFiatInputValue,
		setIsFiatMode,
		setMaxCryptoLimit,
		setMaxLimit,
		setMinLimitCrypto,
		setMinLimitFiat,
		switchCurrencies
	};
};
